import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import { fromJS, Map, Set, List } from "immutable";
import { ProductCollectionItem, DATA_DIR } from "./product-collection-item";
import { Dialog, ConfirmDialog } from "components/dialog";
import { Item, CollectionAction, IMAGE_URL, SizeTableTypes } from "constants/product-collection";
import { ModalTitle } from "./modal-title";
import { IDisplayFields, ISchemaKey, parseSizePacks } from "helpers/product-collection";
import {
    IAllocationDimensionValue,
    IAllocationDimension,
    AllocationBag,
    getBagValuesByRowId,
    AllocationBagType,
} from "helpers/allocation-dimension";
import { IDoor } from "helpers/door-collection";
import { IGridParamsQueryParam, IPreference } from "../product-collection-grid-view/tool-panels";
import { isMobile } from "constants/media";
import { amphibiousGetIn } from "helpers/objUtils";
import * as SentryUtil from "lib/sentry-util";
import { AssortmentTransactions, specifyAssortmentTransaction } from "constants/sentry-metrics";
import AppBar from "@material-ui/core/AppBar";
import Tab from "@material-ui/core/Tab";
import TabContext from "@material-ui/lab/TabContext";
import TabList from "@material-ui/lab/TabList";
import TabPanel from "@material-ui/lab/TabPanel";
import { withStyles } from "@material-ui/styles";
import { theme } from "styles/theme";
import { ProductDetails } from "./product-details";
import { merge, $, media } from "glamor";
import { ProductCollectionItemAllocation } from "../../product-collection-item-allocation";
import PropTypes from "prop-types";
import { IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import { ProductCollectionItemsAllocationTransposed } from "components/product-collection/product-collection-items-allocation-transposed";
import { DATATEST } from "constants/integration";
import { ViewportDetectComponent } from "./viewport-detect-component";
import { ICurrency } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";
import { IPackInfo } from "reducers/collection";
import { SizeActions } from "constants/analytics";
import { buildServerRollupRowNodeId, ISnapshotItem } from "helpers/server-rollup";
import { GridApi } from "ag-grid-community";
import { IGridContext } from "../product-collection-grid-view";
import { getNewDimensionFilters } from "../product-collection-grid-view/product-collection-grid-view-helper";
import { IMutateAllocationBagValue } from "actions/collections";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { KEYBOARD_KEYS } from "constants/keyboard";

export const StyledAppBar = withStyles({
    root: {
        margin: "0 -24px",
        width: "auto",
        padding: "0 24px",
        boxShadow: "none",
        backgroundColor: "white",
    },
})(AppBar);

export const StyledTabs = withStyles({
    root: {
        minHeight: "35px",
    },
    indicator: {
        backgroundColor: theme.palette.accent1Color,
    },
    flexContainer: {
        justifyContent: "center",
    },
})(TabList);

export const StyledTab = withStyles({
    root: {
        padding: "0 12px 6px",
        minHeight: "35px",
        fontFamily: "'Lato', sans-serif",
    },
})(Tab);

export const StyledTabPanel = withStyles({
    root: {
        padding: "24px 0 0",
        minHeight: "120px",
    },
})(TabPanel);

export enum NavigateDirection {
    Left = "left",
    Right = "right",
}

export const NAV_PROPS = {
    left: {
        [DATA_DIR]: NavigateDirection.Left,
    },
    right: {
        [DATA_DIR]: NavigateDirection.Right,
    },
};

interface IProps {
    itemId?: string;
    collectionId: string;
    itemCollectionId: string;
    onCreateCollectionAsset: any;
    onMutateCollectionBudget: (collectionId: string, value: any) => void;
    onMutateCollectionItem: any;
    onClearAllocations: (itemId: string) => void;
    onLookupAssortmentErrors: (assortmentsData?: Array<{ [key: string]: string }>) => void;
    items?: List<any>;
    itemsId: string;
    schema: any;
    onRequestClose: () => void;
    onRequestCloseWithConflict: () => void;
    hashtags: Set<string>;
    row?: any;
    rowIndex?: number;
    editable: boolean;
    onOpenDeleteConfirmation: (itemsid: string, itemIndex) => void;
    onOpenCloneConfirmation: () => void;
    onLinkProductToItem?: (itemId: string) => void;
    allowedValueFields: any;
    router: any;
    onOpenProductModalOnProduct: (productId: string) => void;
    copiedAllocation: Map<string, any> | null;
    copiedAllocationName: string;
    copiedAllocationSizes: string;
    copyAllocation: (
        copiedAllocation: Map<string, any> | null,
        copiedAllocationName: string,
        copiedAllocationSizes: string,
        copiedBulks?: Map<string, any> | null
    ) => void;
    pasteAllocation: (row: any, rowIndex: number) => void;
    onSnackbarAdd: any;
    brandEntities: any;
    detailFields: IDisplayFields;
    allocationEnabled: boolean;
    sizingAllocationEnabled: boolean;
    sizingCurveEnabled: boolean;
    sizingRangeEnabled: boolean;
    dates?: IAllocationDimensionValue[];
    doors?: IDoor[];
    doorClassificationSchema?: ISchemaKey[];
    dimensionsPanelApplied: DimensionsPanelReduxState;
    currentAllocationDimensionView?: IPreference[];
    gridParams?: IGridParamsQueryParam;
    onFetchCollectionPOI: (poi: string) => void;
    onColumnGroupOpened: (groupId: string, isExpanded: boolean) => void;
    isRollupMode?: boolean;
    allocatingAssortment: boolean;
    findingSizes: boolean;
    onAllocate: (action: CollectionAction, ids: string[]) => void;
    onMatchSelectedSizesWithSizeRanges: (action: CollectionAction, ids: string[]) => void;
    allocationDimensions?: IAllocationDimension[];
    itemsRevision: number;
    orderedItems?: List<any>;
    onOpenProductModal: (id: string, assortmentId?: string) => void;
    isCollectionUploading: boolean;
    merchantPurchaseOrderDoorGroups: IPurchaseOrderDoorGroup[];
    merchantFeaturePreference?: nuorder.collection.merchant_feature_config.MerchantFeatureConfig;
    itemDisplayProperty?: List<any>;
    sizeTableType?: string;
    merchantCurrency: ICurrency;
    poiToItemsToSizeBulksMap?: Map<string, Map<string, number>>;
    sizePacks: { [key: string]: { [key1: string]: IPackInfo } };
    sizePackEnabled?: boolean;
    canShopBrand?: boolean;
    onAcceptPackUnits: () => void;
    snapshotItems?: Map<string, ISnapshotItem>;
    isServerRollup?: boolean;
    gridApi: GridApi;
    onSetServerRollupSnapshotItem: (itemId: string, collectionId: string, product: ISnapshotItem) => void;
    onMutateBagValues: (newBagValues: IMutateAllocationBagValue) => void;
    allocationBagRef?: {
        allocationBag: AllocationBag;
    };
    assortmentDimensionMetrics?: boolean;
    nodeRowIndex?: number;
    rootGridApi: GridApi;
}

enum TabValues {
    Overview = "overview",
    Details = "details",
    Sizing = "sizing",
}

interface IState {
    isDeleteImageModalOpen: boolean;
    selectedImageUrl?: string;
    selectedTab: TabValues;
    mainGridContext?: Pick<IGridContext, "dimensionFilters" | "allocationBagRef">;
}

export class ProductCollectionItemModal extends React.PureComponent<IProps, IState> {
    state: IState = {
        isDeleteImageModalOpen: false,
        selectedImageUrl: undefined,
        selectedTab: TabValues.Overview,
    };

    styles = merge(
        $(" .navArrow > button", {
            backgroundColor: `${theme.palette.canvasColor} !important`,
        }),
        $(" .disabledNavArrow > button", {
            backgroundColor: `${theme.palette.grey} !important`,
        }),
        $(" .navArrow > button svg", {
            color: `${theme.palette.darkGrey} !important`,
            fill: `${theme.palette.darkGrey} !important`,
        }),
        $(" .borderLeftClass", {
            borderLeft: `2px solid ${theme.grid.sectionSeparatorColor}`,
        }),
        media(
            "(max-width: 580px)",
            $(" .navArrow", {
                display: "none !important",
            })
        )
    );

    muiStyles = {
        customContentStyle: {
            width: "calc(100vw - 20px)",
            maxWidth: "initial",
        },
        mobileCustomContentStyle: {
            width: "calc(100vw - 10px)",
            maxWidth: "initial",
        },
    };

    paperProps = {
        style: { margin: "0 0" },
    };

    mobilePaperProps = {
        style: { margin: "0" },
    };

    static contextTypes = {
        viewport: PropTypes.string.isRequired,
    };

    private isMobileiOS: boolean;
    private nodeRowIndex: number;

    handleKeyDown = (e: KeyboardEvent) => {
        const disabledElements = ["input", "textarea", "select", "option", "button"];
        const targetElement = e && (e.target as HTMLElement);
        if (targetElement && e.key === KEYBOARD_KEYS.ESCAPE) {
            if (
                !disabledElements.includes(targetElement.tagName.toLowerCase()) &&
                !targetElement.contentEditable?.includes("true")
            ) {
                this.props.onRequestClose();
            }

            targetElement.blur();
        }
    };

    getItemsByFilter = (filter) => {
        const { items } = this.props;
        return items?.reduce((acc: List<any>, item: Map<string, any>) => {
            const matches = Object.entries(filter).reduce((acc, [key, value]) => {
                return acc && item.getIn(["fields", key, "string_value"]) === value;
            }, true);
            return matches ? acc.push(item) : acc;
        }, List());
    };

    getFilter = (itemId) => {
        const { itemDisplayProperty, items } = this.props;
        const item = items?.find((item) => {
            return item?.getIn(["fields", "id", "string_value"]) === itemId;
        });

        return itemDisplayProperty?.reduce((acc, property) => {
            const isSizeFilter = property?.getIn(["detail_view", "is_size_filter"], false);
            const fieldName = property?.getIn(["field_name"]);
            return isSizeFilter ? { ...acc, [fieldName]: item?.getIn(["fields", fieldName, "string_value"]) } : acc;
        }, {});
    };
    componentDidUpdate(prevProps: IProps, prevState: IState) {
        const { itemId, dimensionsPanelApplied, gridParams, allocationDimensions } = this.props;

        if (itemId && itemId !== prevProps.itemId) {
            process.nextTick(() => {
                SentryUtil.finishTransaction(AssortmentTransactions.OpenCDP);
            });
        }
        if (!itemId && prevProps.itemId) {
            this.setState({ selectedTab: TabValues.Overview });
        }

        if (prevState.selectedTab !== this.state.selectedTab) {
            process.nextTick(() => {
                SentryUtil.finishTransaction(
                    specifyAssortmentTransaction(AssortmentTransactions.CDPTabChange, this.state.selectedTab)
                );
            });
        }

        if (
            dimensionsPanelApplied !== prevProps.dimensionsPanelApplied ||
            gridParams !== prevProps.gridParams ||
            allocationDimensions !== prevProps.allocationDimensions
        ) {
            this.setState({
                mainGridContext: {
                    dimensionFilters: getNewDimensionFilters(allocationDimensions, gridParams, dimensionsPanelApplied),
                    allocationBagRef: this.props.allocationBagRef,
                },
            });
        }
    }

    UNSAFE_componentWillMount() {
        document.addEventListener("keydown", this.handleKeyDown);
    }
    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown);
    }

    UNSAFE_componentWillReceiveProps(nextProps: IProps) {
        if (
            (nextProps.itemId && nextProps.itemId !== this.props.itemId) ||
            (nextProps.row &&
                this.props.row &&
                nextProps.row.getIn(Item.FIELDS.PRODUCT_ID) !== this.props.row.getIn(Item.FIELDS.PRODUCT_ID)) ||
            (nextProps.isServerRollup &&
                nextProps.snapshotItems.get(buildServerRollupRowNodeId(nextProps.itemId, nextProps.itemCollectionId)) &&
                !this.props.snapshotItems.get(
                    buildServerRollupRowNodeId(this.props.itemId, this.props.itemCollectionId)
                ))
        ) {
            this.setState({ selectedImageUrl: this.getDefaultImageUrl(nextProps) });
            this.isMobileiOS = /iPad|iPhone/.test(window.navigator.userAgent);
        }

        if (this.props.itemId && nextProps.itemId && this.props.row && !nextProps.row) {
            this.props.onRequestCloseWithConflict();
        }
    }

    handleCopyAllocation = () => {
        const { row, copyAllocation, detailFields } = this.props;
        const isLegacyBag = !this.props.allocationBagRef;
        const rowId = amphibiousGetIn(row, Item.FIELDS.ID);
        const allocations = isLegacyBag
            ? amphibiousGetIn(row, Item.ALLOCATIONS)
            : getBagValuesByRowId(AllocationBagType.allocations, this.props.allocationBagRef.allocationBag, rowId);
        const bulks = isLegacyBag
            ? amphibiousGetIn(row, Item.BULKS)
            : getBagValuesByRowId(AllocationBagType.bulks, this.props.allocationBagRef.allocationBag, rowId);

        if (allocations) {
            const isTitle = row.getIn(detailFields.is_title.path);
            const allocationName = isTitle && isTitle.length > 8 ? `${isTitle.slice(0, 8).trim()}...` : isTitle;
            const sizes = row.getIn(Item.FIELDS.SIZES);
            copyAllocation(allocations, allocationName, sizes, bulks);
        } else {
            this.props.onSnackbarAdd({ message: formatMessage("There is no allocation saved to this row.") });
        }
    };

    handlePasteAllocation = () => {
        const { row, rowIndex, pasteAllocation } = this.props;
        pasteAllocation(row, rowIndex);
    };

    handleFindSizeRanges = () => {
        const { findingSizes, row, onMatchSelectedSizesWithSizeRanges } = this.props;
        const id = row.getIn(Item.FIELDS.ID);
        const action = findingSizes ? CollectionAction.Stop : CollectionAction.Start;
        onMatchSelectedSizesWithSizeRanges(action, [id]);

        if (!findingSizes) {
            const analytics = (global as any).analytics as any;
            analytics.track(SizeActions.FindSizeClicked, {
                rowId: id,
            });
        }
    };

    handleAllocate = () => {
        const { row, allocatingAssortment, onAllocate } = this.props;
        const id = row.getIn(Item.FIELDS.ID);
        const action = allocatingAssortment ? CollectionAction.Stop : CollectionAction.Start;
        onAllocate(action, [id]);
    };

    getMatchingSizePacks = (item: Map<string, any>) => {
        const { sizePacks } = this.props;
        const packIds = parseSizePacks(amphibiousGetIn(item, Item.FIELDS.PACK_IDS)) ?? [];
        const brandId = amphibiousGetIn(item, Item.FIELDS.BRAND_ID);
        return packIds.filter((packId) => !!sizePacks[brandId]?.[packId]).map((packId) => sizePacks[brandId]?.[packId]);
    };

    render() {
        const { viewport } = this.context;
        const { mainGridContext } = this.state;
        const {
            itemId,
            onRequestClose,
            itemDisplayProperty,
            items,
            itemsId,
            collectionId,
            onCreateCollectionAsset,
            onMutateCollectionItem,
            schema,
            hashtags,
            editable,
            onOpenDeleteConfirmation,
            allowedValueFields,
            router,
            onOpenProductModalOnProduct,
            copiedAllocationName,
            copiedAllocation,
            brandEntities,
            detailFields,
            doors,
            dates,
            doorClassificationSchema,
            dimensionsPanelApplied,
            gridParams,
            allocationEnabled,
            sizingAllocationEnabled,
            sizingCurveEnabled,
            sizingRangeEnabled,
            onColumnGroupOpened,
            isRollupMode,
            findingSizes,
            allocatingAssortment,
            allocationDimensions,
            itemsRevision,
            orderedItems,
            onClearAllocations,
            onLookupAssortmentErrors,
            currentAllocationDimensionView,
            isCollectionUploading,
            merchantPurchaseOrderDoorGroups,
            merchantFeaturePreference,
            sizeTableType,
            merchantCurrency,
            poiToItemsToSizeBulksMap,
            onFetchCollectionPOI,
            sizePackEnabled = false,
            canShopBrand,
            sizePacks,
            onAcceptPackUnits,
            snapshotItems,
            isServerRollup,
            itemCollectionId,
            onMutateBagValues,
            allocationBagRef,
            assortmentDimensionMetrics,
            rootGridApi,
        } = this.props;
        const filters = this.getFilter(itemId);
        const isGroupedSizeTable = sizeTableType === SizeTableTypes.Grouped;
        const { isDeleteImageModalOpen } = this.state;
        const isLandscape = window.orientation === 90 || window.orientation === -90;
        const readOnly = !editable;

        const screenHeight = isLandscape ? screen.width : screen.height;
        const minHeight = screenHeight - 200;
        const bodyStyle = { borderTop: undefined, minHeight: "80vh" } as any;
        if (this.isMobileiOS) {
            bodyStyle.minHeight = `${minHeight}px`;
        }

        const matchingItem = this.getRowById(itemId);
        const matchingSizePacks = this.getMatchingSizePacks(matchingItem);
        const modalOpen = !!itemId && !!matchingItem;
        let thereIsAllocation = false;
        if (matchingItem) {
            if (!allocationBagRef) {
                thereIsAllocation = !!matchingItem.getIn(Item.ALLOCATIONS);
            } else {
                const bagValues = getBagValuesByRowId(
                    AllocationBagType.allocations,
                    allocationBagRef.allocationBag,
                    itemId
                );
                thereIsAllocation = bagValues && bagValues.size > 0;
            }
        }
        const hasSizing = sizingAllocationEnabled && allocationEnabled && (doors?.length ?? 0) > 0;

        if (!modalOpen) {
            return null;
        }
        return (
            <React.Fragment>
                <Dialog
                    open={modalOpen}
                    contentStyle={
                        isMobile(viewport) ? this.muiStyles.mobileCustomContentStyle : this.muiStyles.customContentStyle
                    }
                    modal={false}
                    repositionOnUpdate={false}
                    bodyStyle={bodyStyle}
                    showCloseButton={false}
                    paperProps={isMobile(viewport) ? this.mobilePaperProps : this.paperProps}
                    title={
                        <ModalTitle
                            readOnly={readOnly}
                            onCloneItem={this.handleCloneItem}
                            onRequestClose={onRequestClose}
                            onRemoveItem={this.handleRemoveItem}
                            onNavigateProduct={this.handleNavigateProduct}
                            copiedAllocationName={copiedAllocationName}
                            copiedAllocation={copiedAllocation}
                            copyAllocation={this.handleCopyAllocation}
                            pasteAllocation={this.handlePasteAllocation}
                            findingSizes={findingSizes}
                            allocatingAssortment={allocatingAssortment}
                            allocate={this.handleAllocate}
                            findSizeRanges={this.handleFindSizeRanges}
                            sizingAllocationEnabled={sizingAllocationEnabled}
                            sizingCurveEnabled={sizingCurveEnabled}
                            sizingRangeEnabled={sizingRangeEnabled}
                            thereIsAllocation={thereIsAllocation}
                            getSelectedImageUrl={this.getSelectedImageUrl}
                            item={matchingItem}
                            detailFields={detailFields}
                            brandEntities={brandEntities}
                            itemDisplayProperty={itemDisplayProperty}
                            onAcceptPackUnits={onAcceptPackUnits}
                            sizePackEnabled={sizePackEnabled}
                            isServerRollup={isServerRollup}
                        />
                    }
                >
                    <div {...this.styles}>
                        <div className="tabs">
                            <TabContext value={this.state.selectedTab}>
                                <StyledAppBar position="sticky" color="default">
                                    <StyledTabs
                                        onChange={this.handleTabChange}
                                        aria-label={formatMessage("Cart Details Page Navigation")}
                                    >
                                        <StyledTab
                                            data-test={DATATEST.ASSORTMENTS.CDP.OVERVIEW_TAB}
                                            value={TabValues.Overview}
                                            label={formatMessage("Overview")}
                                        />
                                        <StyledTab
                                            data-test={DATATEST.ASSORTMENTS.CDP.SIZING_TAB}
                                            value={TabValues.Sizing}
                                            label={formatMessage("Sizing")}
                                            style={{ display: hasSizing ? "block" : "none" }}
                                        />
                                        <StyledTab
                                            data-test={DATATEST.ASSORTMENTS.CDP.DETAILS_TAB}
                                            value={TabValues.Details}
                                            label={formatMessage("Details")}
                                        />
                                    </StyledTabs>
                                </StyledAppBar>
                                <StyledTabPanel value={TabValues.Overview}>
                                    <ProductCollectionItem
                                        collectionId={collectionId}
                                        fieldLookup="image_url"
                                        itemId={itemId}
                                        onCreateCollectionAsset={onCreateCollectionAsset}
                                        onMutateCollectionItem={onMutateCollectionItem}
                                        deleteImageModalOpen={isDeleteImageModalOpen}
                                        onOpenDeleteImageModal={this.handleOpenDeleteImageModal}
                                        items={items}
                                        itemsId={itemsId}
                                        readOnly={readOnly}
                                        setSelectedImage={this.handleSetSelectedImage}
                                        selectedImageUrl={this.state.selectedImageUrl}
                                        getSelectedImageUrl={this.getSelectedImageUrl}
                                        onOpenDeleteConfirmation={onOpenDeleteConfirmation}
                                        onLinkProductToItem={this.handleLinkProductToItem}
                                        router={router}
                                        onOpenProductModalOnProduct={onOpenProductModalOnProduct}
                                        brandEntities={brandEntities}
                                        detailFields={detailFields}
                                        orderedItems={orderedItems}
                                        isCollectionUploading={isCollectionUploading}
                                        merchantCurrency={merchantCurrency}
                                        isRollupMode={isRollupMode}
                                        canShopBrand={canShopBrand}
                                        itemDisplayProperty={itemDisplayProperty}
                                        snapshotItems={snapshotItems}
                                        isServerRollup={isServerRollup}
                                        itemCollectionId={itemCollectionId}
                                        rootGridApi={rootGridApi}
                                    />
                                </StyledTabPanel>
                                <StyledTabPanel value={TabValues.Details}>
                                    <ProductDetails
                                        itemsId={itemsId}
                                        item={matchingItem}
                                        items={items}
                                        itemId={itemId}
                                        readOnly={readOnly}
                                        schema={schema}
                                        onUpdateItem={onMutateCollectionItem}
                                        hashtags={hashtags}
                                        allowedValueFields={allowedValueFields}
                                        isServerRollup={isServerRollup}
                                        context={mainGridContext}
                                    />
                                </StyledTabPanel>
                                {hasSizing && (
                                    <StyledTabPanel value={TabValues.Sizing}>
                                        <ViewportDetectComponent
                                            title={formatMessage("Your browser is too small")}
                                            message={formatMessage(
                                                "Resize your browser to be at least 768px wide to edit item's allocation"
                                            )}
                                        >
                                            {!isGroupedSizeTable && (
                                                <ProductCollectionItemAllocation
                                                    itemDisplayProperty={itemDisplayProperty}
                                                    item={matchingItem}
                                                    itemsRevision={itemsRevision}
                                                    onMutateCollectionItem={this.handleAllocationMutate}
                                                    onClearAllocations={onClearAllocations}
                                                    onLookupAssortmentErrors={onLookupAssortmentErrors}
                                                    readOnly={readOnly}
                                                    dates={dates}
                                                    doors={doors}
                                                    currentAllocationDimensionView={currentAllocationDimensionView}
                                                    doorClassificationSchema={doorClassificationSchema}
                                                    dimensionsPanelApplied={dimensionsPanelApplied}
                                                    gridParams={gridParams}
                                                    onColumnGroupOpened={onColumnGroupOpened}
                                                    collectionId={collectionId}
                                                    isRollupMode={isRollupMode}
                                                    isServerRollup={isServerRollup}
                                                    sizingAllocationEnabled={sizingAllocationEnabled}
                                                    allocationDimensions={allocationDimensions}
                                                    editable={editable}
                                                    isMobile={isMobile(viewport)}
                                                    merchantPurchaseOrderDoorGroups={merchantPurchaseOrderDoorGroups}
                                                    merchantFeaturePreference={merchantFeaturePreference}
                                                    poiToItemsToSizeBulksMap={poiToItemsToSizeBulksMap}
                                                    onFetchCollectionPOI={onFetchCollectionPOI}
                                                    matchingSizePacks={matchingSizePacks}
                                                    sizePacks={sizePacks}
                                                    sizePackEnabled={sizePackEnabled}
                                                    onMutateBagValues={onMutateBagValues}
                                                    allocationBagRef={allocationBagRef}
                                                    assortmentDimensionMetrics={assortmentDimensionMetrics}
                                                    merchantCurrency={merchantCurrency}
                                                    rootGridApi={rootGridApi}
                                                />
                                            )}
                                            {isGroupedSizeTable && (
                                                <ProductCollectionItemsAllocationTransposed
                                                    item={matchingItem}
                                                    items={this.getItemsByFilter(filters)}
                                                    itemsRevision={itemsRevision}
                                                    onMutateCollectionItem={this.handleAllocationMutate}
                                                    onClearAllocations={onClearAllocations}
                                                    readOnly={readOnly}
                                                    dates={dates}
                                                    doors={doors}
                                                    currentAllocationDimensionView={currentAllocationDimensionView}
                                                    doorClassificationSchema={doorClassificationSchema}
                                                    dimensionsPanelApplied={dimensionsPanelApplied}
                                                    gridParams={gridParams}
                                                    onColumnGroupOpened={onColumnGroupOpened}
                                                    collectionId={collectionId}
                                                    isRollupMode={isRollupMode}
                                                    sizingAllocationEnabled={sizingAllocationEnabled}
                                                    allocationDimensions={allocationDimensions}
                                                    editable={editable}
                                                    isMobile={isMobile(viewport)}
                                                    imageSchema={itemDisplayProperty
                                                        .find((schema) => schema.get("field_name") === IMAGE_URL)
                                                        .toJS()}
                                                    variationSchema={(
                                                        itemDisplayProperty.find((schema) =>
                                                            amphibiousGetIn(schema, ["detail_view", "is_variation"])
                                                        ) ||
                                                        itemDisplayProperty.find((schema) =>
                                                            amphibiousGetIn(schema, ["detail_view", "is_title"])
                                                        )
                                                    )?.toJS()}
                                                    otherSchemas={itemDisplayProperty
                                                        .filter((schema) =>
                                                            amphibiousGetIn(schema, [
                                                                "detail_view",
                                                                "is_size_table_column",
                                                            ])
                                                        )
                                                        .toJS()}
                                                    sizePackEnabled={sizePackEnabled}
                                                    onMutateBagValues={onMutateBagValues}
                                                    allocationBagRef={allocationBagRef}
                                                    rootGridApi={rootGridApi}
                                                />
                                            )}
                                        </ViewportDetectComponent>
                                    </StyledTabPanel>
                                )}
                            </TabContext>
                        </div>
                    </div>
                </Dialog>
                <ConfirmDialog
                    title={formatMessage("Delete Image?")}
                    open={isDeleteImageModalOpen}
                    onConfirm={this.handleConfirmDeleteImage}
                    onCancel={this.closeDeleteImageModal}
                />
            </React.Fragment>
        );
    }

    private handleAllocationMutate = (item: any) => {
        const { itemsId, onMutateCollectionItem } = this.props;
        const rowIndex = this.getRowIndexById(item.getIn(Item.FIELDS.ID));
        onMutateCollectionItem(itemsId, rowIndex, item);
    };

    private handleTabChange = (_e, tab: TabValues) => {
        SentryUtil.startTransaction(specifyAssortmentTransaction(AssortmentTransactions.CDPTabChange, tab));
        this.setState({ selectedTab: tab });
    };

    private handleOpenDeleteImageModal = () => {
        this.setState({ isDeleteImageModalOpen: true });
    };

    private closeDeleteImageModal = () => {
        this.setState({ isDeleteImageModalOpen: false });
    };

    private handleCloneItem = () => {
        this.props.onOpenCloneConfirmation();
    };

    private handleLinkProductToItem = () => {
        this.props.onLinkProductToItem(this.props.itemId);
    };

    private handleRemoveItem = () => {
        const { itemsId, itemId, onOpenDeleteConfirmation } = this.props;
        onOpenDeleteConfirmation(itemsId, [itemId]);
    };

    private handleConfirmDeleteImage = () => {
        const { itemsId, row, rowIndex } = this.props;
        const selectedImageUrl = this.getSelectedImageUrl();
        let item = row;
        if (item && selectedImageUrl) {
            item = item.toJS();
            const images = this.getImages("image_urls");
            const additionalImages = this.getImages("additional_image_urls");
            if (item.fields.image_url && item.fields.image_url.string_value === selectedImageUrl) {
                delete item.fields.image_url;
            }

            if (additionalImages.includes(selectedImageUrl)) {
                // user can only delete uploaded images
                const index = additionalImages.indexOf(selectedImageUrl);
                const imageUrls = item.fields.additional_image_urls.string_value.split(",");
                imageUrls.splice(index, 1);

                if (imageUrls.length) {
                    item.fields.additional_image_urls = { string_value: imageUrls.join(",") };
                } else {
                    delete item.fields.additional_image_urls;
                }

                item = fromJS(item);
                this.closeDeleteImageModal();
                this.props.onMutateCollectionItem(itemsId, rowIndex, item);
                let uploadedImagesAfterDelete = item.getIn(Item.FIELDS.ADDITIONAL_IMAGE_URLS);
                if (uploadedImagesAfterDelete) {
                    uploadedImagesAfterDelete = uploadedImagesAfterDelete.split(",");
                } else {
                    uploadedImagesAfterDelete = [];
                }
                const allImages = images.concat(uploadedImagesAfterDelete);
                this.setState({ selectedImageUrl: allImages[0] });
            }
        }
    };

    private handleSetSelectedImage = (selectedImageUrl) => {
        this.setState({ selectedImageUrl });
    };

    private getDefaultImageUrl = (props = this.props) => {
        const { row, isServerRollup, itemId } = props;
        let selectedImageUrl = row ? row.getIn(Item.FIELDS.IMAGE_URL) : "";
        if (isServerRollup && itemId) {
            const item = this.getRowById(itemId, props);
            selectedImageUrl = item && item.getIn(Item.FIELDS.IMAGE_URL);
        }

        const images =
            !selectedImageUrl &&
            this.getImages("image_urls", props).concat(this.getImages("additional_image_urls", props));
        return selectedImageUrl ? selectedImageUrl : images[0];
    };

    private getSelectedImageUrl = (props = this.props) => {
        let { selectedImageUrl } = this.state;
        const images = this.getImages("image_urls", props).concat(this.getImages("additional_image_urls", props));
        if (!selectedImageUrl && images.length) {
            selectedImageUrl = images[0];
        }

        return selectedImageUrl;
    };

    private getImages(key: string, props = this.props) {
        const { row, itemId, isServerRollup, snapshotItems, itemCollectionId } = props;
        let rowCSV;
        if (isServerRollup) {
            const item = snapshotItems.get(buildServerRollupRowNodeId(itemId, itemCollectionId)) as any;
            rowCSV = item && item.getIn(["fields", key, "string_value"]);
        } else {
            rowCSV = row && row.getIn(["fields", key, "string_value"]);
        }

        if (rowCSV) {
            return rowCSV.split(",");
        }
        return [];
    }

    private getRowIndexById = (id, items = this.props.items) => {
        // gets row index determined by collection not ag-grid
        return items?.findIndex((item) => amphibiousGetIn(item, Item.FIELDS.ID) === id);
    };

    private getRowById = (id, props = this.props) => {
        const { isServerRollup, snapshotItems, itemCollectionId } = props;
        if (isServerRollup) {
            return snapshotItems.get(buildServerRollupRowNodeId(id, itemCollectionId));
        }
        // gets row index determined by collection not ag-grid
        const rowIndex = this.getRowIndexById(id);
        if (rowIndex >= 0) {
            return props.items.get(rowIndex);
        }
        return null;
    };

    private handleNavigateProduct = ({ currentTarget }) => {
        const direction = currentTarget.getAttribute(DATA_DIR);

        if (this.props.isServerRollup) {
            this.handleNavigateProductInRollup(direction);
        } else {
            this.handleNavigateProductInAssortment(direction);
        }
    };

    private handleNavigateProductInAssortment = (direction: NavigateDirection) => {
        const { itemId, orderedItems, onOpenProductModal } = this.props;
        const sortedIndex = this.getRowIndexById(itemId, orderedItems);

        let nextIndex = -1;
        if (direction === NavigateDirection.Left && sortedIndex !== 0) {
            nextIndex = sortedIndex - 1;
        } else if (direction === NavigateDirection.Right && sortedIndex !== orderedItems.size - 1) {
            nextIndex = sortedIndex + 1;
        }
        const nextItem = nextIndex >= 0 && orderedItems.get(nextIndex);
        if (nextItem) {
            const nextItemId = amphibiousGetIn(nextItem, Item.FIELDS.ID);
            onOpenProductModal(nextItemId);
        }
    };

    private handleNavigateProductInRollup = (direction: NavigateDirection) => {
        const { onOpenProductModal, gridApi, onSetServerRollupSnapshotItem, nodeRowIndex } = this.props;
        const currentRowNode = gridApi.getModel().getRow(this.nodeRowIndex ?? nodeRowIndex);
        const newIndex = currentRowNode.rowIndex + (direction === NavigateDirection.Left ? -1 : 1);
        const nextItem = gridApi.getModel().getRow(newIndex);

        // Show product only when there is next or previous product
        if (nextItem && !nextItem.group) {
            this.nodeRowIndex = newIndex;
            onSetServerRollupSnapshotItem(nextItem.data?.id, nextItem.data?.collection_id, nextItem.data);
            onOpenProductModal(nextItem.data?.id, nextItem.data?.collection_id);
        }
    };
}
